import React from "react";
import {
  SafetyInitiative,
} from "../../util/certificateGenerator";
import "../../styles/certificate.css";

const LOGO = `${process.env.PUBLIC_URL || ""}/logo.png`;

const Shield: React.FC<{
  color: string;
  gradientId: string;
  children: React.ReactNode;
}> = ({ color, gradientId, children }) => (
  <svg viewBox="0 0 100 110" className="cert-shield" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
        <stop offset="100%" stopColor={color} stopOpacity="0.1" />
      </linearGradient>
    </defs>
    <path
      d="M50 4 L92 18 V56 C92 82 72 100 50 106 C28 100 8 82 8 56 V18 Z"
      fill={`url(#${gradientId})`}
      stroke={color}
      strokeWidth="2.5"
    />
    <g transform="translate(50 55)" stroke={color} fill={color}>
      {children}
    </g>
  </svg>
);

const StarIcon = (
  <g fill="currentColor" stroke="none">
    <polygon points="0,-14 4,-4 14,-4 6,3 9,13 0,7 -9,13 -6,3 -14,-4 -4,-4" />
  </g>
);
const PhoneCarIcon = (
  <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="-14" y="-12" width="10" height="18" rx="2" />
    <line x1="-12" y1="3" x2="-6" y2="3" />
    <path d="M2 6 L4 -2 L14 -2 L16 6 Z" fill="currentColor" stroke="none" />
    <circle cx="5" cy="8" r="2" fill="currentColor" />
    <circle cx="13" cy="8" r="2" fill="currentColor" />
  </g>
);
const HardHatIcon = (
  <g fill="currentColor" stroke="none">
    <path d="M-14 6 C-14 -4 -6 -10 0 -10 C6 -10 14 -4 14 6 Z" />
    <rect x="-16" y="6" width="32" height="4" rx="1.5" />
    <rect x="-3" y="-10" width="6" height="4" />
  </g>
);
const ArchieIcon = (
  <g fill="currentColor" stroke="none">
    <circle cx="0" cy="-6" r="6" />
    <path d="M-10 12 C-10 4 -4 -1 0 -1 C4 -1 10 4 10 12 Z" />
    <circle cx="-2.5" cy="-7" r="1.2" fill="#0a1830" />
    <circle cx="2.5" cy="-7" r="1.2" fill="#0a1830" />
  </g>
);
const ThreatIcon = (
  <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="0" cy="0" r="13" />
    <line x1="-9" y1="-9" x2="9" y2="9" />
    <path d="M-6 -3 L0 -8 L6 -3" />
    <circle cx="0" cy="3" r="2" fill="currentColor" />
  </g>
);
const LoneWorkerIcon = (
  <g fill="currentColor" stroke="none">
    <circle cx="0" cy="-8" r="5" />
    <path d="M-8 12 C-8 2 -4 -2 0 -2 C4 -2 8 2 8 12 Z" />
    <circle cx="11" cy="-4" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="11" y1="-7" x2="11" y2="-1" stroke="currentColor" strokeWidth="2" />
  </g>
);
const RideWatchIcon = (
  <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M-12 4 L-10 -4 L10 -4 L12 4 Z" fill="currentColor" />
    <circle cx="-7" cy="6" r="2.5" fill="currentColor" />
    <circle cx="7" cy="6" r="2.5" fill="currentColor" />
    <path d="M0 -10 L0 -6" />
    <circle cx="0" cy="-12" r="2" fill="currentColor" />
  </g>
);
const HeartShieldIcon = (
  <g fill="currentColor" stroke="none">
    <path d="M0 10 C-10 4 -12 -4 -8 -8 C-4 -12 -1 -8 0 -5 C1 -8 4 -12 8 -8 C12 -4 10 4 0 10 Z" />
  </g>
);
const ReportIcon = (
  <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="-9" y="-12" width="18" height="22" rx="2" fill="currentColor" />
    <line x1="-5" y1="-6" x2="5" y2="-6" stroke="#0a1830" />
    <line x1="-5" y1="-1" x2="5" y2="-1" stroke="#0a1830" />
    <line x1="-5" y1="4" x2="2" y2="4" stroke="#0a1830" />
  </g>
);
const EmergencyPlanIcon = (
  <g fill="currentColor" stroke="none">
    <circle cx="0" cy="0" r="13" />
    <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="800" fill="#0a1830">
      !
    </text>
  </g>
);

const iconById: Record<string, React.ReactNode> = {
  "safety-ambassador": StarIcon,
  "distracted-driving": PhoneCarIcon,
  "mobile-workplace": HardHatIcon,
  "archie-plus": ArchieIcon,
  "ai-threat-detector": ThreatIcon,
  "lone-worker": LoneWorkerIcon,
  ridewatch: RideWatchIcon,
  "mental-health-shield": HeartShieldIcon,
  "dol-digital-reporter": ReportIcon,
  "workplace-emergency": EmergencyPlanIcon,
};

interface CertificateCardProps {
  cert: SafetyInitiative;
  recipientName: string;
  elementId?: string;
  className?: string;
}

const CertificateCard: React.FC<CertificateCardProps> = ({
  cert,
  recipientName,
  elementId,
  className = "",
}) => (
  <article
    id={elementId}
    className={`certificate ${className}`.trim()}
    style={
      {
        ["--cert-color" as any]: cert.themeColor,
      } as React.CSSProperties
    }
  >
    <div className="certificate__inner">
      <div className="certificate__corner certificate__corner--tl" />
      <div className="certificate__corner certificate__corner--tr" />
      <div className="certificate__corner certificate__corner--bl" />
      <div className="certificate__corner certificate__corner--br" />

      <header className="certificate__header">
        <img
          src={LOGO}
          alt="OnMyWay Logo"
          className="certificate__logo"
          crossOrigin="anonymous"
        />
      </header>

      <div className="certificate__title-block">
        <h2 className="certificate__title">{cert.title.toUpperCase()}</h2>
        <div className="certificate__divider">
          <span />
          <h3 className="certificate__subtitle">{cert.subtitle.toUpperCase()}</h3>
          <span />
        </div>
      </div>

      <div className="certificate__body">
        <p className="certificate__intro">This certificate recognizes</p>
        <h4 className="certificate__name">{recipientName}</h4>
        <div className="certificate__name-underline" />
        <p className="certificate__description">{cert.recognitionText}</p>
      </div>

      <div className="certificate__icon-wrap" aria-hidden="true">
        <Shield color={cert.themeColor} gradientId={`shield-${cert.id}`}>
          {iconById[cert.id] || StarIcon}
        </Shield>
      </div>

      <footer className="certificate__footer">
        <p className="certificate__signature">OnMyWay Team</p>
        <div className="certificate__sig-line" />
        <p className="certificate__sig-caption">OnMyWay Safety Initiative</p>
      </footer>

      <div className="certificate__swoosh" />
      <div className="certificate__laurel certificate__laurel--left" />
      <div className="certificate__laurel certificate__laurel--right" />
    </div>
  </article>
);

export default CertificateCard;
